import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import MultiSelect from 'core/multiSelect';
import { SemanticUI } from '@iterable/deprecated_react_components';
import moment from 'moment';
import { DateRangePickerWithCollapsibleCalendar } from 'ui/datePicker/DateRangePickerWithCollapsibleCalendar';
import ReportModalContainer from './ReportModal';
import {
  fetchReportingTypeTable,
  updateReportFilters,
  clearReportingTypeModalIds,
  updateReportParams,
  updateMetricsList,
  saveReportingTypeModalIds,
} from '../actions';
import {
  getActiveReport,
  selectLoadingHighLevelMetrics,
  selectSelectedHighLevelMetrics,
  selectReportDataSourceModal,
  selectFormattedHighLevelMetrics,
} from '../selectors';

const getTimestamp = (date) => (date ? date.valueOf() : null);

const maxWidth = '767px';
const StyledSegment = styled(SemanticUI.Segment)`
  && {
    background: #eff0f0;
  }
`;

const MenuItemContainer = styled.div`
  display: flex;

  @media (max-width: ${maxWidth}) {
    display: block;
    flex-wrap: wrap;
  }
`;

const StyledMenuItemWrapper = styled.div`
  display: flex;
  margin: 0 5px;
  .ui.label > .fitted.icon {
    margin: 0;
    width: auto;
  }

  /* stylelint-disable selector-pseudo-class-no-unknown */
  button:global(.ui),
  :global(.ui.search.dropdown[role='combobox']) {
    margin: 0;
  }

  @media (max-width: ${maxWidth}) {
    display: block;
    &:not(:last-child) {
      margin-bottom: 5px;
    }
  }
`;

const StyledDateRangeContainer = styled(StyledMenuItemWrapper)`
  flex: 1 1 40%;
`;

const StyledMetricsDropdownContainer = styled(StyledMenuItemWrapper)`
  flex: 0 1 40%;
  max-width: 400px;

  @media (max-width: ${maxWidth}) {
    max-width: unset;
  }
`;

export const ReportFilters = () => {
  const report = useSelector(getActiveReport) || {};
  const reportingTypeTable = useSelector(selectReportDataSourceModal);
  const loadingHighLevelMetrics = useSelector(selectLoadingHighLevelMetrics);
  const selectedMetrics = useSelector(selectSelectedHighLevelMetrics);
  const highLevelMetrics = useSelector(selectFormattedHighLevelMetrics).filter(
    (metric) =>
      metric.value !== 'OpenRateSegmentAnalysis' ||
      selectedMetrics.includes('OpenRateSegmentAnalysis'),
  );
  /**
  State
  */
  const [isReportModalOpen, toggleReportModal] = useState(false);
  const [originalComponentType, setOriginalComponentType] = useState('');

  const dispatch = useDispatch();

  const closeAndSaveModalIds = (selectedTypeIdsInModal, id, componentType) => {
    dispatch(saveReportingTypeModalIds(selectedTypeIdsInModal, id, componentType));
    toggleReportModal(false);
  };

  const closeReportModal = () => {
    dispatch(clearReportingTypeModalIds());
    dispatch(
      updateReportFilters({
        id: report.id,
        componentType: originalComponentType,
      }),
    );

    toggleReportModal(false);
    setOriginalComponentType('');
  };

  const openReportModal = (componentType) => {
    dispatch(fetchReportingTypeTable(componentType, reportingTypeTable));
    toggleReportModal(true);
    setOriginalComponentType(componentType);
  };

  if (!report) return null;

  const { id, rangeStart, rangeEnd } = report;

  // Convert start and end date to moment objects if they exist
  const startDate = (rangeStart && moment(rangeStart)) || rangeStart;
  const endDate = (rangeEnd && moment(rangeEnd)) || rangeEnd;

  return (
    <StyledSegment attached="top">
      <MenuItemContainer>
        <div className="menuItemWrapper reportModalButtonContainer">
          <ReportModalContainer
            report={report}
            isReportModalOpen={isReportModalOpen}
            openReportModal={openReportModal}
            closeReportModal={closeReportModal}
            closeAndSaveModalIds={closeAndSaveModalIds}
            clearDataSourceIdsInModal={() => dispatch(clearReportingTypeModalIds())}
            reportingTypeTable={reportingTypeTable}
          />
        </div>
        <StyledDateRangeContainer>
          <DateRangePickerWithCollapsibleCalendar
            placeholder="Select Date range"
            startDate={startDate}
            endDate={endDate}
            startTime={startDate}
            endTime={endDate}
            onDateRangeChange={({ startDate: sd, endDate: ed }) => {
              dispatch(
                updateReportParams(id, {
                  rangeStart: getTimestamp(sd),
                  rangeEnd: getTimestamp(ed),
                }),
              );
            }}
            isTimeZonePickerDisabled
            blockFutureDates
          />
        </StyledDateRangeContainer>
        <StyledMetricsDropdownContainer>
          <MultiSelect
            filterPlaceholder="Filter Metrics"
            placeholder={loadingHighLevelMetrics ? 'Loading metrics...' : 'Select metrics'}
            onChange={(value) => dispatch(updateMetricsList(id, value))}
            options={highLevelMetrics}
            optionName="Metric"
            optionsName="Metrics"
            overflowAmount={4}
            value={selectedMetrics}
          />
        </StyledMetricsDropdownContainer>
      </MenuItemContainer>
    </StyledSegment>
  );
};

export default ReportFilters;
